import { AnimatePresence, motion } from 'framer-motion';
import { entries } from 'lodash';
import MarkdownIt from 'markdown-it';
import Quill from 'quill';
import Delta from 'quill-delta';
import {
	type ReactNode,
	forwardRef,
	useCallback,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalStorage } from 'react-use';
import { Box, Flex } from 'reflexbox';

import { useAuthenticatedUser, useFeature } from '@treams/treams-hooks';
import {
	CommentTargetType,
	CreateCommentInput,
	CreateCommentMutationResult,
	FeatureName,
	useCreateCommentMutation,
} from '@treams/treams-network';
import { AICard, Button, Paragraph, QuillInput } from '@treams/treams-ui';

type CommentComposerProps = {
	targetID: string;
	targetType: CommentTargetType;
	metadata?: Record<string, string>;
	autoFocus?: boolean;
	secondaryAction?: ReactNode;
	onCreate: (
		comment: NonNullable<CreateCommentMutationResult['data']>['createComment']['comment'],
	) => void;
	onCancel?: () => void;
	onRefresh?: () => void;
};

export type CommentComposerHandle = {
	setComment: (comment: Delta) => void;
	setMessage: (message: string) => void;
	setInsight: (markdown: string) => void;
};

const CommentComposer = forwardRef<CommentComposerHandle, CommentComposerProps>(
	(
		{ targetID, targetType, metadata, autoFocus, secondaryAction, onCreate, onRefresh, onCancel },
		ref,
	) => {
		const { t } = useTranslation();
		useAuthenticatedUser();
		const hasAIInsights = useFeature(FeatureName.ConversationAiInsights);
		const [create] = useCreateCommentMutation();
		const [comment, setComment, removeFromLocalStorage] = useLocalStorage<undefined | Delta>(
			`comments-${targetID}`,
			undefined,
		);
		const [loading, setLoading] = useState(false);
		const [editorKey, setEditorKey] = useState<'normal' | 'reloading'>('normal');
		const quillRef = useRef<Quill>();

		const reset = () => {
			setComment(undefined);
			setLoading(false);
			removeFromLocalStorage();
			onRefresh?.();
			setInsightCollapsed(true);
			setEditorKey('reloading');

			/**
			 * This is necessary because the component is not re-rendering when the comment is empty.
			 * This is a known issue with QuillInput.
			 */
			setTimeout(() => {
				setEditorKey('normal');
			}, 500);
		};

		const onChange = useCallback(
			(value: Delta) => {
				setComment(value);
				console.log('onChange', value);
			},
			[setComment],
		);

		const onSubmit = async () => {
			if (!comment) return;

			setLoading(true);

			const input: CreateCommentInput = {
				targetID,
				targetType,
				content: comment,
				metadata:
					metadata &&
					entries(metadata).map(([key, value]) => ({
						key,
						value,
					})),
			};

			const { data } = await create({
				variables: {
					input,
				},
			});

			if (data) {
				onCreate(data.createComment.comment);
			}

			reset();
		};

		const commentIsEmpty =
			comment == null || (comment?.ops.length === 1 && comment.ops[0].insert === '\n');

		const [toolbarMessage, setToolbarMessage] = useState<string | undefined>(undefined);
		const [insight, setInsight] = useState<string | undefined>(undefined);
		const [insightCollapsed, setInsightCollapsed] = useState(false);
		const md = useMemo(() => new MarkdownIt(), []);
		useImperativeHandle(ref, () => ({
			setComment: (comment: Delta) => {
				quillRef.current?.setContents(comment, 'api');
				setComment(comment);
			},
			setMessage: (message: string) => {
				setToolbarMessage(message);

				setTimeout(() => {
					setToolbarMessage(undefined);
				}, 2000);
			},
			setInsight: (markdown: string) => {
				setInsight(markdown);
				setInsightCollapsed(false);
			},
		}));

		return (
			<Flex>
				<Box flexGrow={1} flexShrink={1}>
					<QuillInput
						key={editorKey}
						autoFocus={autoFocus}
						value={comment}
						onChange={onChange}
						onQuill={(q) => (quillRef.current = q)}
						toolbarMessage={toolbarMessage}
					/>
					<Flex mt={2} alignItems="center">
						<Box flexGrow={1} minWidth={0}>
							<AnimatePresence>
								{!commentIsEmpty && (
									<motion.div
										initial={{ opacity: 0 }}
										animate={{ opacity: 1 }}
										exit={{ opacity: 0 }}
										transition={{ duration: 0.2, ease: 'easeInOut' }}
									>
										<Paragraph small style={{ opacity: 0.6 }}>
											{t('comments.composer.autosave')}
										</Paragraph>
									</motion.div>
								)}
							</AnimatePresence>
						</Box>
						{secondaryAction}
						{onCancel && (
							<Button
								ml={2}
								title={t('comments.composer.cancel')}
								type="secondary"
								disabled={loading}
								onClick={() => onCancel?.()}
							/>
						)}
						<Button
							ml={secondaryAction || onCancel ? 2 : 0}
							loading={loading}
							title={t('comments.composer.create')}
							onClick={onSubmit}
						/>
					</Flex>
					{hasAIInsights && insight && (
						<Box mt={3}>
							<AICard
								markdown
								title={t('oneOnOnes.templates.insightTitle')}
								collapsed={insightCollapsed}
								onHeaderClick={() => setInsightCollapsed((v) => !v)}
							>
								<div dangerouslySetInnerHTML={{ __html: md.render(insight) }} />
							</AICard>
						</Box>
					)}
				</Box>
			</Flex>
		);
	},
);

CommentComposer.displayName = 'CommentComposer';

export default CommentComposer;
